import { useState } from "react";

import Button from "~/components/buttons/Button";
import Card from "~/components/Card";
import CardValue from "~/components/Card/components/CardValue.tsx";
import Headline from "~/components/Headline";
import Hint from "~/components/Hint";
import {
	FIXED_PRICE_OPTIONS,
	INVOICE_BY_MAIL_OPTIONS,
	MONTHLY_BILLING_OPTIONS,
	PROJECT_BILLING_SETTINGS_COMBINE_ORDERS_OPTIONS,
	PROJECT_BILLING_SETTINGS_INVOICE_TYPE_OPTIONS,
	SKILL_BASED_OPTIONS,
	TRAVEL_EXPENSES_INCLUDED_OPTIONS,
} from "~/constants/form.ts";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import type { BillingSettings } from "~/modules/billing/api/billingSettings/billingSettingsTypes.ts";
import { invoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import { useBillingSettingsLookups } from "~/modules/billing/hooks/useBillingSettingsLookups.ts";
import { resolveBillingSettingsDisplay } from "~/modules/billing/utils/billingSettingsDisplayUtils.ts";
import { useClient } from "~/modules/client/api/client/clientQueries.ts";
import BillingSettingsContactPersonDisplay
	from "~/modules/project/components/ProjectDetailsView/components/ProjectBillingSectionTabContent/components/ProjectBillingSettingsTabContent/components/BillingSettingsContactPersonDisplay.tsx";
import BillingSettingsInvoiceRecipientDisplay
	from "~/modules/project/components/ProjectDetailsView/components/ProjectBillingSectionTabContent/components/ProjectBillingSettingsTabContent/components/BillingSettingsInvoiceRecipientDisplay.tsx";
import {
	DivergingOrderBillingSettingsTable,
} from "~/modules/project/components/ProjectDetailsView/components/ProjectBillingSectionTabContent/components/ProjectBillingSettingsTabContent/components/DivergingOrderBillingSettingsTable/DivergingOrderBillingSettingsTable.tsx";
import UpdateProjectBillingSettingsSidebar
	from "~/modules/project/components/ProjectDetailsView/components/ProjectBillingSectionTabContent/components/ProjectBillingSettingsTabContent/components/UpdateProjectBillingSettingsSidebar/UpdateProjectBillingSettingsSidebar.tsx";
import LoadingPage from "~/pages/LoadingPage.tsx";
import { resolveOptionLabel } from "~/utils/form/formUtils.ts";
import { nl2br } from "~/utils/stringUtils.tsx";


type Props = {
	projectBillingSettings: BillingSettings
	projectId: string;
	clientId: string;
};

const ProjectBillingSettings: React.FunctionComponent<Props> = ({ projectBillingSettings, clientId }) => {
	const [showUpdateBillingSettingsSidebar, setShowUpdateBillingSettingsSidebar] = useState<boolean>(false);

	const { data: clientData } = useClient(clientId);
	const lookups = useBillingSettingsLookups(clientId);
	const {
		clientContactPerson,
		currencyDisplayName,
		emailRecipients,
		invoiceRecipient,
	} = resolveBillingSettingsDisplay(projectBillingSettings, lookups);


	const isSelfBilled =
		projectBillingSettings.invoiceTypeId === invoiceTypeId.SelfBilledInvoice;

	const contactLabel = !isSelfBilled ? "Rechnungsempfänger" : "Ansprechpartner";

	const isLoading = lookups.isLoading;

	const missingInvoiceRecipientOrContactPerson = !isLoading && !invoiceRecipient && !clientContactPerson;

	if (isLoading) {
		return <LoadingPage />;
	}

	return (
		<div className="w-full overflow-y-auto pb-10 pt-4">
			<div className="mb-4 flex items-center justify-between">
				<Headline type="h4">Globale Projekteinstellungen</Headline>
				<div className="flex items-center gap-3">
					<Button
						size="sm"
						onClick={() => setShowUpdateBillingSettingsSidebar(true)}
					>
						Bearbeiten
					</Button>
				</div>
			</div>

			<div className="grid grid-cols-2 gap-4">
				<Card title="Rechnungstyp">
					{resolveOptionLabel(
						projectBillingSettings.invoiceTypeId ?? invoiceTypeId.RegularInvoice,
						PROJECT_BILLING_SETTINGS_INVOICE_TYPE_OPTIONS,
					)}
				</Card>
				<Card title="Sammelrechnung für alle Phasen">
					{resolveOptionLabel(
						!(projectBillingSettings.billOrdersSeparately ?? false),
						PROJECT_BILLING_SETTINGS_COMBINE_ORDERS_OPTIONS,
					)}
				</Card>
				{!isSelfBilled && (
					<Card title="Abrechnung">
						<dl className="grid grid-cols-2 gap-x-6 gap-y-4">
							<CardValue label="Abrechnungsart">
								{resolveOptionLabel(
									projectBillingSettings.isFixedPrice ?? false,
									FIXED_PRICE_OPTIONS,
								)}
							</CardValue>
							<CardValue label="Abrechnungskategorie ">
								{resolveOptionLabel(
									projectBillingSettings.isSkillBased ?? false,
									SKILL_BASED_OPTIONS,
								)}
							</CardValue>
							<CardValue label="Reisekosten">
								{resolveOptionLabel(
									projectBillingSettings.travelExpensesIncluded ?? false,
									TRAVEL_EXPENSES_INCLUDED_OPTIONS,
								)}
							</CardValue>
							<CardValue label="Abrechnungsintervall">
								{resolveOptionLabel(
									projectBillingSettings.isBilledMonthly ?? false,
									MONTHLY_BILLING_OPTIONS,
								)}
							</CardValue>
							<CardValue label="Zahlungsziel">
								{`${projectBillingSettings.paymentTargetDays ?? 30} Tage`}
							</CardValue>
							{currencyDisplayName && (
								<CardValue label="Währung">
									{currencyDisplayName}
								</CardValue>
							)}
						</dl>
					</Card>
				)}
				<Card title={contactLabel}>
					{clientContactPerson && clientData &&
						<BillingSettingsContactPersonDisplay
							client={clientData}
							clientContactPerson={clientContactPerson}
							isSelfBilledInvoice={isSelfBilled}
						/>}
					{invoiceRecipient &&
						<BillingSettingsInvoiceRecipientDisplay invoiceRecipient={invoiceRecipient}
																isSelfBilledInvoice={isSelfBilled} />
					}
					{missingInvoiceRecipientOrContactPerson &&
						<Hint theme="error">Es wurde kein Rechnungsempfänger oder Ansprechpartner gefunden. Bitte
							überprüfe die Einstellungen.</Hint>}
				</Card>

				{!isSelfBilled && (
					<Card title="Rechnungsversand">
						<dl className="grid grid-cols-2 gap-x-6 gap-y-4">
							<CardValue label="Versand per">

								{resolveOptionLabel(
									projectBillingSettings.invoiceByMail ?? false,
									INVOICE_BY_MAIL_OPTIONS,
								)}
							</CardValue>
							<CardValue label="Weitere E-Mail-Empfänger"
									   enableCopyToClipboard={emailRecipients.length > 0}>
								{emailRecipients.length > 0 ? emailRecipients.join(", ") : EMPTY_VALUE_PLACEHOLDER}
							</CardValue>
						</dl>
					</Card>
				)}
				<Card title="Hinweise zur Abrechnung">
					<p className="whitespace-pre-line">
						{projectBillingSettings.billingInstructions ? nl2br(projectBillingSettings.billingInstructions) : EMPTY_VALUE_PLACEHOLDER}
					</p>
				</Card>
			</div>
			<div className="mt-4 space-y-2">
				<Headline type="h4">Abweichende Einstellungen in Bestellungen</Headline>
				<DivergingOrderBillingSettingsTable projectBillingSettings={projectBillingSettings}
													projectId={projectBillingSettings.ownerId}
													clientId={clientId} />
			</div>
			<UpdateProjectBillingSettingsSidebar
				clientId={clientId}
				projectBillingSettings={projectBillingSettings}
				isOpen={showUpdateBillingSettingsSidebar}
				onClose={() => setShowUpdateBillingSettingsSidebar(false)}
			/>
		</div>
	);
};

export default ProjectBillingSettings;